import React, { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CheckIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { pageKey } from '@dashboard/lib/queryKeys';
import { cn } from '@dashboard/lib/utils';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { Command, CommandEmpty, CommandItem, CommandList } from '@dashboard/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent } from '@dashboard/components/ui/popover';
import { Input } from '@dashboard/components/ui/input';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { Label } from '@/dashboard/components/Form';

const PageFilterItemSkeleton = () => (
  <div className="flex items-center gap-2 px-2 py-2">
    <div className="min-w-0 flex-1 space-y-1.5">
      <Skeleton className="h-3.5 w-3/5" />
      <Skeleton className="h-3 w-2/5" />
    </div>
  </div>
);

const PageFilterInput = ({
  placeholder = 'Filter by page',
  label,
  name,
  onChange,
  pageTypes = ['checkout'],
  productTypes = [],
  initialValue,
}) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [selectedPage, setSelectedPage] = useState();
  const { searchInput, setSearchInput, search } = useSearchInput('');

  const { getPages, getPage } = usePageQueries();
  const { sellerId } = useAuth();

  // Keep latest onChange in a ref so we never fire effects that depend on its
  // identity. Callers don't need to memoize the callback they pass in.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const { data: currentPageRedirect } = useQuery({
    queryKey: pageKey(value),
    queryFn: () => getPage(value),
    enabled: !!value,
  });

  useEffect(() => {
    if (!selectedPage && currentPageRedirect) {
      setSelectedPage(currentPageRedirect);
      setSearchInput(currentPageRedirect.name);
    }
  }, [selectedPage, currentPageRedirect, setSearchInput]);

  useEffect(() => {
    if (!selectedPage) return;
    if (selectedPage._id !== value) setValue(selectedPage._id);
  }, [selectedPage, value]);

  const effectiveSearch = selectedPage || searchInput === '' ? '' : search;

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['pages', effectiveSearch],
    queryFn: async ({ pageParam }) =>
      await getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        productType: productTypes,
        limit: 10,
        skip: pageParam * 10,
        search: effectiveSearch === '' ? undefined : effectiveSearch,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const hasItems = data?.pages?.[0]?.data?.length > 0;

  const handleSelect = page => {
    setSelectedPage(page);
    setValue(page._id);
    setSearchInput(page.name);
    setOpen(false);
    onChangeRef.current?.(page._id);
  };

  const handleClear = e => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPage(null);
    setValue(null);
    setSearchInput('');
    inputRef.current?.focus();
    onChangeRef.current?.(null);
  };

  return (
    <div>
      {label && <Label>{label}</Label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              name={name}
              value={searchInput}
              placeholder={placeholder}
              autoComplete="off"
              aria-label={placeholder}
              onChange={e => {
                setSearchInput(e.target.value);
                if (selectedPage) setSelectedPage(null);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              className="pr-16"
            />
            <div className="absolute inset-y-0 right-2 flex items-center gap-1 pointer-events-none">
              {searchInput?.length > 0 && (
                <button
                  type="button"
                  onMouseDown={handleClear}
                  className="text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-muted-foreground pointer-events-auto"
                  aria-label="Clear filter"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
              {selectedPage ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-400 dark:text-muted-foreground" />
              ) : (
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-300 dark:text-muted-foreground" />
              )}
            </div>
          </div>
        </PopoverAnchor>

        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => {
            if (inputRef.current && inputRef.current.contains(e.target)) {
              e.preventDefault();
            }
          }}
        >
          <Command shouldFilter={false} value={selectedPage?._id}>
            <CommandList className="max-h-72 p-1 scrollbar-hide">
              {isError && <CommandEmpty>Error loading pages</CommandEmpty>}
              {!isError && !isLoading && !hasItems && <CommandEmpty>No pages found</CommandEmpty>}

              <BottomScrollListener onBottom={fetchNextPage} isLoadingMore={isLoading}>
                {data?.pages?.map(page =>
                  page?.data?.map(item => (
                    <CommandItem
                      key={item._id}
                      value={item._id}
                      onSelect={() => handleSelect(item)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{item.name}</div>
                        {item.type === 'checkout' && (
                          <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-muted-foreground">
                            {getProductPriceLabel(item.product)}
                          </div>
                        )}
                      </div>

                      <CheckIcon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          item._id === selectedPage?._id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  )),
                )}
              </BottomScrollListener>

              {isLoading && (
                <div aria-busy aria-live="polite">
                  <PageFilterItemSkeleton />
                  <PageFilterItemSkeleton />
                  <PageFilterItemSkeleton />
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PageFilterInput;
